import { ReactNode, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';

import { getIdentityBaseUrl } from '@/components/signup/utils/environment';
import { getProductData, ProductData, ProductKey } from '@/components/signup/utils/products';
import { getLoomSsoUrl, getSwitchAccountUrl } from '@/components/signup/utils/url';
import { useUserContext } from '@/components/user-context/user-context-provider';
import {
	AccessRequestCapability,
	CompanySite,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { Button } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';

import { useSiteActivationContext } from '../../hooks/site-activation-context';
import { ExistingUser as DefaultExistingUser } from '../existing-user/existing-user';

export const JoinableSites = ({
	ExistingUser = <DefaultExistingUser />,
}: {
	ExistingUser?: ReactNode;
}) => {
	const [showSiteNaming, setShowSiteNaming] = useState(false);
	const { product, edition, profile } = useUserContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { isWorkspaceProduct } = useSiteActivationContext();
	const {
		user: { email },
	} = useUserContext();

	const { productName } = getProductData(product?.key as keyof typeof ProductData);

	let sites =
		profile.attributes?.existingProductsFromOrg
			.filter(({ accessType }) =>
				[AccessRequestCapability.DIRECT_ACCESS, AccessRequestCapability.REQUEST_ACCESS].includes(
					accessType,
				),
			)
			.sort((a, b) =>
				a.accessType !== b.accessType && a.accessType === AccessRequestCapability.DIRECT_ACCESS
					? -1
					: 1,
			) || [];

	// make sure sites are unique by cloudId (userProfile can return duplicates)
	sites = Array.from(
		sites
			.reduce((acc, currentItem) => {
				if (!acc.has(currentItem.cloudId)) {
					acc.set(currentItem.cloudId, currentItem);
				}
				return acc;
			}, new Map())
			.values(),
	);

	const requestAccess = intl.formatMessage({
		id: 'signup.siteActivation.requestAccess',
		defaultMessage: 'Request access',
	});

	const join = intl.formatMessage({
		id: 'signup.siteActivation.join',
		defaultMessage: 'Join',
	});

	const createNewWorkspaceMessageValues = {
		link: (str: ReactNode) =>
			createNewWorkspaceMessageLink(str, () => {
				setShowSiteNaming(true);
				createAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'joinableSitesGoToSiteNamingScreen',
					attributes: {
						productName: product?.name,
						productKey: product?.key,
						edition,
					},
				}).fire();
			}),
	};

	const createNewWorkspaceMessage = isWorkspaceProduct
		? intl.formatMessage(
				{
					id: 'BxpSignup.SiteExpansionForm.cobrandedConfluenceCreateNewWorkspaceLink',
					defaultMessage: 'or <link>create a new workspace</link>',
				},
				createNewWorkspaceMessageValues,
			)
		: intl.formatMessage(
				{
					id: 'BxpSignup.SiteExpansionForm.cobrandedJiraCreateNewWorkspaceLink',
					defaultMessage: 'or <link>start a new site</link>',
				},
				createNewWorkspaceMessageValues,
			);

	const clickReturnToExistingWorkspace = () => {
		setShowSiteNaming(false);
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'joinableSitesGoToReturnToProductScreen',
			attributes: {
				productName: product?.name,
				productKey: product?.key,
				edition,
			},
		}).fire();
	};

	const goToExistingWorkspaceMessageValues = {
		link: (str: ReactNode) => link(str, clickReturnToExistingWorkspace),
	};

	const goToExistingWorkspaceMessage = isWorkspaceProduct
		? intl.formatMessage(
				{
					id: 'BxpSignup.SiteExpansionForm.cobrandedGoToExistingWorkspace',
					defaultMessage: 'or <link>visit an existing workspace</link>',
				},
				goToExistingWorkspaceMessageValues,
			)
		: intl.formatMessage(
				{
					id: 'bxpSignup.companyExpand.cobrandedReturnToJoinSites',
					defaultMessage: 'or <link>join an existing site</link>',
				},
				goToExistingWorkspaceMessageValues,
			);

	return showSiteNaming ? (
		<div css={containerCss}>
			{ExistingUser}
			<div css={goToExistingWorkspaceMessageCss}>{goToExistingWorkspaceMessage}</div>
		</div>
	) : (
		<div css={containerCss}>
			<p css={switchAccountSubheader}>
				<FormattedMessage
					id="BxpSignup.SiteExpansionForm.cobrandedSubheader.2"
					defaultMessage="You’re logged in as {email}. <switchAccountLink>Switch account</switchAccountLink>"
					values={{
						email: <strong css={emailCss}>{email}</strong>,
						switchAccountLink,
					}}
				/>
			</p>
			<div css={boxContainerCss} data-testid="return-to-product">
				{sites?.map((site, index) => (
					<a
						key={index}
						css={siteBoxCss}
						href={getProductUrl(site)}
						onClick={(e) => {
							e.preventDefault(); // wait for event to fire
							createAnalyticsEvent({
								eventType: 'ui',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId:
									site.accessType === AccessRequestCapability.REQUEST_ACCESS
										? 'joinableSitesClickRequestAccessButton'
										: 'joinableSitesClickJoinSiteButton',
								attributes: {
									productName,
									productKey: site.productKey,
									edition: site.productEdition,
								},
							}).fire();
							window.location.assign(getProductUrl(site));
						}}
					>
						<div css={siteCss}>
							<p css={bold}>{productName}</p>
							<div css={siteNameCss}>{site.displayName}</div>
						</div>
						<div css={goToProductButtonCss}>
							<Button variant="primary" size="small" color="blue">
								{site.accessType === AccessRequestCapability.REQUEST_ACCESS ? requestAccess : join}
							</Button>
						</div>
					</a>
				))}
			</div>
			<div css={createNewWorkspaceMessageCss}>{createNewWorkspaceMessage}</div>
		</div>
	);
};

const getProductUrl = ({ productKey, productUrl, cloudId }: CompanySite) => {
	// loom links go through Loom SSO directly into the workspace
	if (productKey === ProductKey.LOOM) {
		return getLoomSsoUrl(cloudId);
	}

	// jira links should be myjira.atlassian.net/welcome/software
	if (productKey === ProductKey.JIRA_SOFTWARE) {
		const { origin } = new URL(productUrl);
		productUrl = `${origin}/welcome/software`;
	}

	//send to identity login with continue param
	const joinUrl = new URL('/login', getIdentityBaseUrl());
	joinUrl.searchParams.append('continue', productUrl);
	return joinUrl.toString();
};

const link = (str: ReactNode, clickReturnToExistingWorkspace: () => void) => (
	<button css={goToExistingWorkspaceLinkCss} onClick={clickReturnToExistingWorkspace}>
		{str}
	</button>
);

const createNewWorkspaceMessageLink = (str: ReactNode, exitView: () => void) => (
	<button css={createNewWorkspaceLinkCss} onClick={exitView}>
		{str}
	</button>
);

const switchAccountLink = (children: ReactNode) => (
	<a href={getSwitchAccountUrl()} css={switchAccountLinkCss}>
		{children}
	</a>
);

const containerCss = css({
	alignContent: 'space-between',
	display: 'grid',
	flexGrow: 1,
	gap: 32,
});

const goToExistingWorkspaceLinkCss = css({
	backgroundColor: 'transparent !important',
	border: 0,
	borderRadius: 0,
	color: wacToken('color.primary.blue'),
	cursor: 'pointer',
	fontFamily: 'inherit',
	fontSize: 'inherit !important',
	fontWeight: 'inherit',
	minHeight: 'auto',
	minWidth: 'auto',
	padding: '0 !important',
	textDecoration: 'underline !important',
	[':hover']: {
		color: `${wacToken('color.extended.blue800')} !important`,
	},
});

const goToExistingWorkspaceMessageCss = css({
	fontSize: 14,
	fontWeight: 500,
	textAlign: 'center',
});

const switchAccountSubheader = css({
	fontSize: 14,
	margin: 0,
	textAlign: 'center',
});

const createNewWorkspaceMessageCss = css({
	fontSize: 14,
	fontWeight: 500,
	textAlign: 'center',
});

const createNewWorkspaceLinkCss = css({
	backgroundColor: 'transparent !important',
	border: 0,
	borderRadius: 0,
	color: wacToken('color.primary.blue'),
	cursor: 'pointer',
	fontFamily: 'inherit',
	fontSize: 'inherit !important',
	fontWeight: 'inherit',
	minHeight: 'auto',
	minWidth: 'auto',
	padding: '0 !important',
	textDecoration: 'underline !important',
	[':hover']: {
		color: wacToken('color.extended.blue800'),
	},
});

const boxContainerCss = css({
	display: 'grid',
	gap: 16,
	[media.above.sm]: {
		maxHeight: '20.75rem',
		overflowY: 'auto',
	},
});

const siteBoxCss = css({
	alignItems: 'center',
	border: `1px solid ${wacToken('color.neutral.light400')}`,
	borderRadius: 8,
	display: 'grid',
	gap: 10,
	gridTemplateColumns: '1fr auto',
	justifyContent: 'space-between',
	padding: 16,
	width: '100%',
	[':hover']: {
		textDecoration: 'none !important',
	},
});

const goToProductButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		fontFamily: 'inherit !important',
		fontSize: 14,
		maxWidth: 180,
		paddingBlock: 8,
	},
});

const bold = css({
	fontWeight: '400',
	margin: 0,
});

const siteCss = css({
	fontSize: 16,
	overflow: 'hidden',
});

const siteNameCss = css({
	color: wacToken('color.neutral.light700'),
	fontSize: 14,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const emailCss = css({
	fontWeight: 600,
	hyphens: 'auto',
	overflowWrap: 'break-word',
	wordBreak: 'break-word',
	wordWrap: 'break-word',
});

const switchAccountLinkCss = css({
	color: wacToken('color.primary.blue'),
	textDecoration: 'underline',
	whiteSpace: 'nowrap',
	[':hover']: {
		color: wacToken('color.extended.blue800'),
	},
});
